import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatbot_initialize, delete_session, populateAwsDataService, populateAzureDataService, populateGcpDataService, populateOnpremDataService } from '../services';
import { Modal } from 'react-bootstrap';
import NmtAI from '../NmtAI/Nmtai';
import IntegrationCloud from './IntegrationCloud/IntegrationCloud';
import '../styles/dashboardV2.scss';

// Lazy loaded components
const ThirdPartyExternalExposure = lazy(() => import('./ThirdpartyExternalExposure/ThirdPartyExternalExposure'));
const RiskRegistry = lazy(() => import('./RiskRegistry/RiskRegistry'));
const ComplianceReady = lazy(() => import('./ComplianceReady/ComplianceReady'));

const tabItems = [
    { title: 'NOVA AI', key: 'nova' },
    { title: 'Risk Registry', key: 'risk' },
    { title: 'TPCRM', key: 'tpcrm' },
    { title: 'GRC', key: 'grc' },
    { title: 'Integration Cloud', key: 'integrations' },
];

function BankUserDashboard() {
    const [aiInitialized, setAiInitialized] = useState(null);
    const [remainingCredits, setRemainingCredits] = useState(0);
    const [isReady, setIsReady] = useState(false);
    const [activeTab, setActiveTab] = useState('nova');
    const navigate = useNavigate();

    const sectionsRef = {
        nova: useRef(null),
        risk: useRef(null),
        tpcrm: useRef(null),
        grc: useRef(null),
        integrations: useRef(null),
    };

    useEffect(() => {
        const initializeChatbot = async () => {
            try {
                await delete_session();
                const response = await chatbot_initialize();
                const data = response?.statusCode?.response_data?.data;
                const statusCode = response?.statusCode?.response_data?.status_code;
                if (statusCode === 200) {
                    setAiInitialized(data);
                    setRemainingCredits(data?.remaining_credits ?? 0);
                }
            } catch (error) {
                console.error('Error initializing chatbot:', error);
            } finally {
                setIsReady(true);
            }
        };
        initializeChatbot();
    }, []);

    // IntersectionObserver for active tab highlight on scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveTab(entry.target.id);
                    }
                });
            },
            { root: null, threshold: 0.4 }
        );

        Object.keys(sectionsRef).forEach((id) => {
            const el = sectionsRef[id]?.current;
            if (el) observer.observe(el);
        });

        return () => {
            Object.keys(sectionsRef).forEach((id) => {
                const el = sectionsRef[id]?.current;
                if (el) observer.unobserve(el);
            });
        };
    }, [isReady]);

    // Removed handle hash scrolling on load/ready here
    const handleScroll = (e, ref) => {
        e.preventDefault();
        if (ref?.current) {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // Cloud functions moved to IntegrationCloud component

    return (
        <div className='cstm_dashboard_tabs'>
            {/* Tab Navigation */}
            <ul className="tab-navigation sticky-tab">
                {tabItems.map((tab) => (
                    <li key={tab.key}>
                        <a
                            href={`#${tab.key}`}
                            onClick={(e) => handleScroll(e, sectionsRef[tab.key])}
                            className={activeTab === tab.key ? 'active' : ''}
                        >
                            {tab.title}
                        </a>
                    </li>
                ))}
            </ul>

            <div className="tab-contents">
                {/* NOVA AI Section */}
                <div className='scroll_section' id="nova" ref={sectionsRef.nova}>
                    {isReady && (
                        <NmtAI
                            initializedData={aiInitialized}
                            remainingCredits={remainingCredits}
                            onCreditsUpdate={setRemainingCredits}
                        />
                    )}
                </div>

                {/* Risk Registry Section */}
                <div className='scroll_section' id="risk" ref={sectionsRef.risk}>
                    <Suspense fallback={<div></div>}>
                        <RiskRegistry />
                    </Suspense>
                </div>

                {/* TPCRM Section */}
                <div className='scroll_section' id="tpcrm" ref={sectionsRef.tpcrm}>
                    <Suspense fallback={<div></div>}>
                        <ThirdPartyExternalExposure />
                    </Suspense>
                </div>

                {/* GRC Section */}
                <div className='scroll_section' id="grc" ref={sectionsRef.grc}>
                    <Suspense fallback={<div></div>}>
                        <ComplianceReady isBankUser={true} />
                    </Suspense>
                </div>

                {/* Integration Cloud Section */}
                <div className='scroll_section' id="integrations" ref={sectionsRef.integrations}>
                    <IntegrationCloud wrapper={false} />
                </div>
            </div>
        </div>
    );
}

export default BankUserDashboard;

